'use client';

// ============================================================================
// LE FILIGRANE DE MARQUE : le logo en grand, derriere une carte.
// ----------------------------------------------------------------------------
//
//   *** ASSISTANCE REUTILISABLE. Si un autre ecran a besoin de ce filigrane,  ***
//   *** IMPORTEZ-LE, ne le recopiez pas.                                     ***
//
// LE VERROUILLAGE TYPOGRAPHIQUE DU NOM N'EST PAS ICI. Il vit dans
// `components/ui/brand-verrou-marque.tsx` (VerrouMarque, VERROU_GRAND), ecrit
// pour la tete du menu lateral et repris tel quel par les ecrans de connexion :
// une seule implementation, donc deux endroits qui ne peuvent pas diverger.
//
// ----------------------------------------------------------------------------
// CE QUE LE DIRIGEANT A DEMANDE, mot pour mot, le 08/08/2026
//
// « Dans le HUB informatique, mets mieux en evidence, jouer avec la typo Ailerons
//   pourquoi pas, mais ce serait bien de dissocier le mot HUB et reecrire Guard
//   Informatique et mettre le G en plus grand du logo et celui sur fond fonce !! »
//
// Et, dans la foulee, pour le fond de la carte :
//
// « Tu peux creer derriere en grand le fameux filigrane que j'aime bien sur les
//   cartes, c'est trop style, mais pas avec les lettres "GI" mais le logo de Guard »
//
// ----------------------------------------------------------------------------
// LES REGLES TENUES ICI
//
// AUCUNE COULEUR EN DUR : la silhouette prend var(--acc), la couleur d'accent de
// l'espace, avec le violet applicatif en seule valeur de repli. Guard Security
// obtient donc son filigrane bleu sans qu'une ligne change.
// ============================================================================

import { logoPourEmplacement, type JeuLogos, type ReglagesEmplacements } from '@/components/ui/brand-asset-utils';

/**
 * Le filigrane : le logo de la marque, en grand, derriere le contenu.
 *
 * POURQUOI UNE SILHOUETTE, ET PAS L'IMAGE. Un filigrane n'a besoin que de la
 * FORME, pas de l'encre. On applique donc le fichier comme un MASQUE CSS et on
 * peint la surface a la couleur d'accent : la variante clair ou sombre n'a plus
 * aucune importance.
 *
 * C'est decisif dans le cas reel : Guard Informatique ne possede QUE son logo
 * general, dessine en encre foncee pour fond clair. En image brute, il aurait
 * donne un fantome clair et sale derriere une carte sombre. En silhouette, le
 * probleme n'existe pas.
 *
 * Purement decoratif : transparent aux clics, invisible a l'assistance vocale.
 * Il ne peut donc ni gener une saisie ni polluer la lecture d'un formulaire.
 *
 * Rend null quand aucun logo n'est deposé : on n'affiche pas un carre vide, et on
 * ne retombe PAS sur des initiales, que le dirigeant a explicitement ecartees.
 */
export function FiligraneMarque({
  jeu,
  reglagesEmplacements = null,
  /** Part de la largeur du parent occupee par le filigrane. Ignore si `taille` est fourni. */
  echelle = 1.15,
  /**
   * Taille exacte, en longueur CSS (« min(150vmin, 1100px) »).
   *
   * A PREFERER pour un decor de PAGE. Le dirigeant a ete precis : le filigrane
   * doit vivre DERRIERE la carte, sur le fond de la page, et deborder largement
   * autour d'elle, pas se decorer a l'interieur du formulaire. Une part de la
   * largeur du parent ne suffit pas pour cela : il faut pouvoir viser la fenetre.
   */
  taille,
  /** Opacite. Volontairement tres basse : voir la note de contraste ci-dessous. */
  opacite = 0.055,
  /**
   * Ou cadrer le fichier.
   *
   * « signe » (defaut) remplit la surface et garde le HAUT du fichier. C'est ce
   * qui compte pour un logo en hauteur comme celui de Guard Informatique : le
   * fichier empile le bouclier PUIS les mots « GUARD » et « INFORMATIQUE ». Cadre
   * en entier, ces mots se retrouvaient en filigrane DERRIERE les libelles du
   * formulaire, ce qui donne du texte fantome sous du texte. Constate en capture
   * a 1440 px. En gardant le haut, il ne reste que le signe, grand et net.
   *
   * « entier » montre tout le fichier. A reserver a un logo deja carre.
   */
  cadrage = 'signe',
  /**
   * Couleur de la silhouette.
   *
   * Par defaut l'accent de l'espace, ce qui est le bon choix DERRIERE une carte
   * ou sur le fond d'une page : le filigrane s'y lit comme une teinte de la
   * marque. Mais pose SUR un aplat de cette meme couleur (la porte d'univers de
   * la metamorphose), un accent sur de l'accent serait invisible : l'appelant
   * passe alors var(--acc-on), la couleur que le calcul de palette a declaree
   * lisible sur la marque. Le parametre existe pour ce cas, et il ne prend
   * jamais une couleur ecrite en clair : toujours une variable de l'identite.
   */
  couleur = 'var(--acc, #8B5CF6)',
  className,
}: {
  jeu: JeuLogos | null | undefined;
  /** Le choix du dirigeant pour l'emplacement « filigrane » (logoEmplacements). */
  reglagesEmplacements?: ReglagesEmplacements | null;
  echelle?: number;
  taille?: string;
  opacite?: number;
  cadrage?: 'signe' | 'entier';
  couleur?: string;
  className?: string;
}) {
  // L'emplacement « filigrane » : par defaut la variante carree, la plus proche
  // d'un signe, donc la plus reconnaissable en grand. Le fichier ne sert que de
  // DECOUPE (la couleur vient de la marque), mais le choix du dirigeant est
  // respecte comme partout : un seul point de decision, logoPourEmplacement.
  const resolu = jeu ? logoPourEmplacement(jeu, 'filigrane', reglagesEmplacements) : null;
  const adresse = resolu?.url ?? null;
  if (!adresse || resolu?.inexploitable) return null;

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className ?? ''}`}
    >
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{
          width: taille ?? `${Math.round(echelle * 100)}%`,
          aspectRatio: '1 / 1',
          // La silhouette : le fichier ne sert que de decoupe, la couleur vient de
          // la marque. `mask` moderne ET `WebkitMask` : sans le prefixe, Safari
          // afficherait un aplat plein de la couleur d'accent sur toute la surface,
          // ce qui serait bien pire que pas de filigrane.
          backgroundColor: couleur,
          maskImage: `url(${adresse})`,
          WebkitMaskImage: `url(${adresse})`,
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: cadrage === 'signe' ? 'center top' : 'center',
          WebkitMaskPosition: cadrage === 'signe' ? 'center top' : 'center',
          maskSize: cadrage === 'signe' ? 'cover' : 'contain',
          WebkitMaskSize: cadrage === 'signe' ? 'cover' : 'contain',
          opacity: opacite,
        }}
      />
    </div>
  );
}
